import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { KeyRound, Loader2, CheckCircle2, AlertCircle, Zap, RefreshCw, AlertTriangle, Trash2 } from "lucide-react";

export default function Settings() {
  return (
    <div className="max-w-3xl mx-auto p-10">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.18em] mb-3" style={{ color: "var(--rahi-soft)" }}>Account &amp; app</div>
        <h1 className="font-display text-5xl font-black tracking-tight leading-none">Settings</h1>
        <p className="mt-3 text-sm max-w-xl" style={{ color: "var(--rahi-muted)" }}>
          Manage your admin password and app preferences.
        </p>
      </div>
      <ChangePasswordCard/>
      <AutoSyncCard/>
      <DangerZoneCard/>
    </div>
  );
}

function DangerZoneCard() {
  const [phrase, setPhrase] = useState("");
  const [wiping, setWiping] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const REQUIRED = "WIPE ALL RECIPIENTS";

  const submit = async () => {
    setMsg({ type: "", text: "" });
    if (phrase !== REQUIRED) {
      setMsg({ type: "error", text: `Please type exactly: ${REQUIRED}` });
      return;
    }
    if (!window.confirm("This will permanently delete ALL recipients. Campaigns and suppressions are kept. Continue?")) return;
    setWiping(true);
    try {
      const { data } = await api.post("/recipients/wipe", { confirm: REQUIRED });
      setMsg({ type: "success", text: `Deleted ${data.deleted} of ${data.before} recipients.` });
      setPhrase("");
    } catch (e) {
      setMsg({ type: "error", text: formatErr(e) });
    } finally {
      setWiping(false);
    }
  };

  return (
    <section className="rounded-lg border-2 border-red-200 bg-red-50/30 p-6 mt-8" data-testid="danger-zone-card">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={18} className="text-red-600"/>
        <h2 className="font-display text-2xl font-black tracking-tight text-red-700">Danger zone</h2>
      </div>
      <p className="text-sm text-red-700/80 mb-4">
        Wipe the entire recipients collection. Use this before a fresh bulk import.
        Campaigns, suppressions and sync events remain intact.
      </p>
      <label className="block text-xs font-semibold mb-1 text-red-700">Type <code className="bg-red-100 px-1 rounded">{REQUIRED}</code> to confirm</label>
      <input
        className="input mb-2"
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder={REQUIRED}
        data-testid="wipe-confirm-input"
      />
      <button
        onClick={submit}
        disabled={wiping || phrase !== REQUIRED}
        className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 text-white font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700"
        data-testid="wipe-recipients-btn"
      >
        {wiping ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
        {wiping ? "Wiping…" : "Wipe all recipients"}
      </button>
      {msg.text && (
        <div className={`mt-3 text-sm flex items-start gap-2 ${msg.type === "success" ? "text-green-700" : "text-red-700"}`}>
          {msg.type === "success" ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>}
          <span>{msg.text}</span>
        </div>
      )}
    </section>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const strength = passwordStrength(next);

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });
    if (next !== confirm) {
      setMsg({ type: "error", text: "New password and confirmation don't match." });
      return;
    }
    if (next.length < 12) {
      setMsg({ type: "error", text: "New password must be at least 12 characters." });
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setMsg({ type: "ok", text: "Password changed. Your next login will require the new password." });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setMsg({ type: "error", text: formatErr(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-7 mb-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={18} style={{ color: "var(--rahi-red)" }}/>
        <h2 className="font-display text-xl font-black tracking-tight">Change password</h2>
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--rahi-muted)" }}>
        Rotate your admin password without editing environment variables or redeploying.
        Requires 12+ characters with upper, lower, digit and a special character.
      </p>
      <form onSubmit={submit} autoComplete="off" className="space-y-4">
        <div>
          <label className="label">Current password</label>
          <input className="input" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input"/>
        </div>
        <div>
          <label className="label">New password</label>
          <input className="input" type="password" required minLength={12} maxLength={72} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input"/>
          {next && <StrengthMeter score={strength}/>}
        </div>
        <div>
          <label className="label">Confirm new password</label>
          <input className="input" type="password" required minLength={12} maxLength={72} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input"/>
        </div>
        {msg.text && (
          <div className={`text-sm p-3 rounded-md flex items-start gap-2 ${msg.type === "ok" ? "" : ""}`}
               style={{ background: msg.type === "ok" ? "#F0FDF4" : "#FEF2F2", color: msg.type === "ok" ? "#047857" : "#B91C1C" }}
               data-testid="change-password-msg">
            {msg.type === "ok" ? <CheckCircle2 size={16}/> : <AlertCircle size={16}/>}
            <span>{msg.text}</span>
          </div>
        )}
        <button type="submit" disabled={saving || !current || !next || !confirm} className="btn btn-primary" data-testid="change-password-submit-btn">
          {saving ? <><Loader2 size={14} className="animate-spin"/> Changing…</> : "Change password"}
        </button>
      </form>
    </div>
  );
}

function passwordStrength(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 12) s++;
  if (p.length >= 16) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(s, 5);
}

function StrengthMeter({ score }) {
  const labels = ["Very weak", "Weak", "OK", "Good", "Strong", "Excellent"];
  const colors = ["#DC2626", "#F59E0B", "#F59E0B", "#65A30D", "#059669", "#047857"];
  return (
    <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: colors[score] }}>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="h-1 w-8 rounded-full" style={{ background: i <= score ? colors[score] : "var(--rahi-border)" }}/>
        ))}
      </div>
      <span>{labels[score]}</span>
    </div>
  );
}

function AutoSyncCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  const toggle = async (val) => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const { data } = await api.patch("/settings", { auto_sync_suppressions: val });
      setSettings(data);
      setMsg({ type: "ok", text: val ? "Auto-sync ON — will run after every send." : "Auto-sync OFF." });
    } catch (e) {
      setMsg({ type: "error", text: formatErr(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const updateDelay = async (minutes) => {
    setSaving(true); setMsg({ type: "", text: "" });
    try {
      const { data } = await api.patch("/settings", { auto_sync_delay_minutes: minutes });
      setSettings(data);
      setMsg({ type: "ok", text: `Delay updated to ${minutes} minutes.` });
    } catch (e) {
      setMsg({ type: "error", text: formatErr(e.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="card p-7"><Loader2 className="animate-spin" size={16}/></div>;

  const on = !!settings.auto_sync_suppressions;
  const delay = settings.auto_sync_delay_minutes || 30;
  const lastAt = settings.last_auto_sync_at;
  const lastResult = settings.last_auto_sync_result;

  return (
    <div className="card p-7" data-testid="auto-sync-card">
      <div className="flex items-center gap-2 mb-1">
        <Zap size={18} style={{ color: "var(--rahi-red)" }}/>
        <h2 className="font-display text-xl font-black tracking-tight">Auto-sync suppressions</h2>
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--rahi-muted)" }}>
        When ON, the app automatically pulls bounces / unsubscribes / complaints from Mailgun
        a set number of minutes after every campaign completes — so your list stays clean without
        needing to click &ldquo;Sync now&rdquo; manually.
      </p>

      <div className="flex items-center justify-between mb-5 p-4 rounded-md" style={{ background: on ? "#F0FDF4" : "#FBFAF9", border: `1px solid ${on ? "#BBF7D0" : "var(--rahi-border)"}` }}>
        <div>
          <div className="text-sm font-medium">Automatic sync after each send</div>
          <div className="text-xs mt-1" style={{ color: "var(--rahi-muted)" }}>
            {on ? `Will run ${delay} min after every send completes.` : "Currently disabled — use the Sync now button on Sync events."}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => toggle(!on)}
          disabled={saving}
          className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
          style={{ background: on ? "var(--rahi-red)" : "#D6D3D1" }}
          data-testid="auto-sync-toggle"
        >
          <span className="inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform" style={{ transform: on ? "translateX(24px)" : "translateX(4px)" }}/>
        </button>
      </div>

      {on && (
        <div className="mb-4">
          <label className="label">Delay after send (minutes)</label>
          <div className="flex gap-2 items-center">
            {[15, 30, 60, 120].map(m => (
              <button
                key={m}
                onClick={() => updateDelay(m)}
                disabled={saving}
                className={`px-3 py-1.5 rounded-full text-xs border ${delay === m ? "bg-[#FF2101] text-white border-[#FF2101]" : "bg-white hover:bg-[#FBFAF9]"}`}
                style={{ borderColor: delay === m ? "#FF2101" : "var(--rahi-border)", color: delay === m ? "#fff" : "var(--rahi-ink)" }}
                data-testid={`delay-${m}-btn`}
              >
                {m} min
              </button>
            ))}
          </div>
          <p className="hint mt-2">Mailgun typically records bounces within a few minutes but soft bounces take longer. 30 min is a safe default.</p>
        </div>
      )}

      {lastAt && (
        <div className="mt-4 pt-4 border-t text-xs" style={{ borderColor: "var(--rahi-border)", color: "var(--rahi-muted)" }} data-testid="last-auto-sync-info">
          <div className="flex items-center gap-1.5">
            <RefreshCw size={11}/>
            <span>Last auto-sync: <span className="font-mono">{new Date(lastAt).toLocaleString("en-IN")}</span></span>
          </div>
          {lastResult?.applied && (
            <div className="mt-1 font-mono">
              Newly marked — bounces: {lastResult.applied.bounced_marked || 0} · unsubs: {lastResult.applied.unsubscribed_marked || 0} · complaints: {lastResult.applied.complained_marked || 0}
            </div>
          )}
        </div>
      )}

      {msg.text && (
        <div className={`text-sm mt-4 p-3 rounded-md flex items-start gap-2`}
             style={{ background: msg.type === "ok" ? "#F0FDF4" : "#FEF2F2", color: msg.type === "ok" ? "#047857" : "#B91C1C" }}
             data-testid="auto-sync-msg">
          {msg.type === "ok" ? <CheckCircle2 size={16}/> : <AlertCircle size={16}/>}
          <span>{msg.text}</span>
        </div>
      )}
    </div>
  );
}
